const CONTROLLED_SEGMENTS = ['municipal', 'public_private', 'private', 'nursery_public', 'nursery_private', 'maluch_public', 'maluch_private'];
const ALL_DATA_SEGMENTS = [...CONTROLLED_SEGMENTS, 'unverified'];

export const KIDPLACE_PROVIDER = 'Kidplace';
export const NO_COMPETITION = '#cbd7e4';

// One stable map color per brand. Known brands intentionally use a hue that
// resembles the brand/site identity; unknown labels are deterministically
// assigned from the fallback palette so colors never jump between renders.
const BRAND_RULES = [
  { test: /^kidplace$/i, name: 'Kidplace', color: '#f69f1c' },
  { test: /^live\s*kid$/i, name: 'LiveKid', color: '#2badff' },
  { test: /^inso$/i, name: 'INSO', color: '#1757aa' },
  { test: /^4\s*parents$/i, name: '4Parents', color: '#7147A8' },
  { test: /^kids?\s*view$/i, name: 'KidsView', color: '#8A317C' },
  { test: /^kidme$/i, name: 'Kidme', color: '#174AE8' },
  { test: /^kidogo$/i, name: 'Kidogo', color: '#159A78' },
  { test: /^obecnosci(?:\.pl)?$/i, name: 'Obecnosci.pl', color: '#00A3A3' },
  { test: /^punktualny\s+przedszkolak$/i, name: 'Punktualny Przedszkolak', color: '#8d5400' },
  { test: /^i\s*przedszkole$/i, name: 'iPrzedszkole', color: '#7ac143' },
  { test: /^vulcan$/i, name: 'VULCAN', color: '#e33f4a' },
  { test: /^librus$/i, name: 'Librus', color: '#B92D38' },
  { test: /^krakfin$/i, name: 'Krakfin', color: '#117AA3' },
  { test: /^odpn$/i, name: 'ODPN', color: '#637A31' },
  { test: /^rozlicz(?:\.edu\.pl)?$/i, name: 'rozlicz.edu.pl', color: '#8A5B43' },
];

const FALLBACK_COLORS = [
  '#2F6F8F', '#9C4D8B', '#6B7F2C', '#B75D2A', '#4967B1',
  '#168C8C', '#9A6A18', '#7B54A4', '#397C57', '#A64752',
];

function normalizeKey(value) {
  return String(value || '')
    .trim()
    .toLocaleLowerCase('pl-PL')
    .replace(/\s+/g, ' ');
}

function hashString(value) {
  let hash = 2166136261;
  const text = normalizeKey(value);
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash >>> 0);
}

function hexToRgb(hex) {
  const value = String(hex || '').replace('#', '');
  if (!/^[0-9a-f]{6}$/i.test(value)) return [203, 215, 228];
  return [0, 2, 4].map((offset) => parseInt(value.slice(offset, offset + 2), 16));
}

function rgbToHex(rgb) {
  return `#${rgb.map((channel) => Math.max(0, Math.min(255, Math.round(channel))).toString(16).padStart(2, '0')).join('')}`;
}

function activeDataSegments(selected = []) {
  const normalized = CONTROLLED_SEGMENTS.filter((segment) => selected.includes(segment));
  return normalized.length === CONTROLLED_SEGMENTS.length ? ALL_DATA_SEGMENTS : normalized;
}

export function canonicalProvider(provider) {
  const text = String(provider || '').trim();
  if (!text) return null;
  const known = BRAND_RULES.find((rule) => rule.test.test(text));
  return known ? known.name : text;
}

export function providerColor(provider) {
  const text = String(provider || '').trim();
  if (!text) return NO_COMPETITION;
  const known = BRAND_RULES.find((rule) => rule.test.test(text));
  if (known) return known.color;
  return FALLBACK_COLORS[hashString(text) % FALLBACK_COLORS.length];
}

export function providerSoftColor(provider, whiteRatio = 0.70) {
  if (!provider) return '#e6edf4';
  const rgb = hexToRgb(providerColor(provider));
  const ratio = Math.max(0, Math.min(0.92, Number(whiteRatio)));
  return rgbToHex(rgb.map((channel) => channel * (1 - ratio) + 255 * ratio));
}

export function providerTextColor(provider) {
  if (!provider) return '#28445f';
  const [r, g, b] = hexToRgb(providerColor(provider));
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.66 ? '#173b5d' : '#ffffff';
}

export function facilityCompetitionProvider(item) {
  if (item?.is_own_customer) return KIDPLACE_PROVIDER;
  return canonicalProvider(item?.competition_provider);
}

function finalizeCounts(countMap) {
  const providers = [...countMap.entries()]
    .filter(([, count]) => Number(count) > 0)
    .map(([provider, count]) => ({
      provider: canonicalProvider(provider) || String(provider),
      count: Number(count || 0),
    }))
    .reduce((acc, row) => {
      const key = normalizeKey(row.provider);
      const existing = acc.get(key) || { provider: row.provider, count: 0 };
      existing.count += row.count;
      acc.set(key, existing);
      return acc;
    }, new Map());

  const sorted = [...providers.values()].sort((a, b) => (
    b.count - a.count || a.provider.localeCompare(b.provider, 'pl')
  ));
  const known = sorted.reduce((sum, row) => sum + row.count, 0);
  const own = sorted.find((row) => normalizeKey(row.provider) === normalizeKey(KIDPLACE_PROVIDER))?.count || 0;
  return {
    providers: sorted,
    known_units_count: known,
    own_units_count: own,
    competitor_units_count: Math.max(0, known - own),
    dominant_provider: sorted[0]?.provider || null,
    dominant_count: sorted[0]?.count || 0,
  };
}

export function combineCompetitionBuckets(buckets = {}, selectedSegments = []) {
  const counts = new Map();
  activeDataSegments(selectedSegments).forEach((segment) => {
    Object.entries(buckets?.[segment] || {}).forEach(([provider, count]) => {
      const name = canonicalProvider(provider) || String(provider || '').trim();
      if (!name) return;
      counts.set(name, Number(counts.get(name) || 0) + Number(count || 0));
    });
  });
  return finalizeCounts(counts);
}

export function competitionSummaryForItems(items = [], scope = 'commune') {
  const counts = new Map();
  if (scope === 'commune') {
    items.forEach((item) => {
      const provider = facilityCompetitionProvider(item);
      if (!provider) return;
      counts.set(provider, Number(counts.get(provider) || 0) + 1);
    });
  } else {
    items.forEach((item) => {
      (item?.competition_summary?.providers || []).forEach(({ provider, count }) => {
        const name = canonicalProvider(provider) || provider;
        if (!name) return;
        counts.set(name, Number(counts.get(name) || 0) + Number(count || 0));
      });
    });
  }
  return finalizeCounts(counts);
}

export function competitionValueText(item, scope = 'commune') {
  if (scope === 'commune') return facilityCompetitionProvider(item) || 'Brak znanej konkurencji';
  const summary = item?.competition_summary;
  if (!summary?.known_units_count) return 'Brak znanych wdrożeń';
  const provider = summary.dominant_provider || 'Znana konkurencja';
  return `${provider} · ${summary.known_units_count}`;
}

export function competitionTooltip(item, scope = 'commune') {
  if (!item) return 'Brak danych';
  if (scope === 'commune') return `${item.name} — ${competitionValueText(item, scope)}`;
  const providers = item?.competition_summary?.providers || [];
  if (!providers.length) return `${item.name} — brak znanych wdrożeń`;
  return `${item.name} — ${providers.map((row) => `${row.provider}: ${row.count}`).join(' · ')}`;
}

export function sortByCompetition(items = [], scope = 'commune') {
  return [...items].sort((a, b) => {
    const aKnown = scope === 'commune' ? Number(Boolean(facilityCompetitionProvider(a))) : Number(a?.competition_summary?.known_units_count || 0);
    const bKnown = scope === 'commune' ? Number(Boolean(facilityCompetitionProvider(b))) : Number(b?.competition_summary?.known_units_count || 0);
    if (scope !== 'commune' && bKnown !== aKnown) return bKnown - aKnown;
    if (scope === 'commune' && bKnown !== aKnown) return bKnown - aKnown;
    const ap = scope === 'commune' ? facilityCompetitionProvider(a) : a?.competition_summary?.dominant_provider;
    const bp = scope === 'commune' ? facilityCompetitionProvider(b) : b?.competition_summary?.dominant_provider;
    return String(ap || '').localeCompare(String(bp || ''), 'pl') || String(a?.name || '').localeCompare(String(b?.name || ''), 'pl');
  });
}
